import { getDeviceHealthStatus } from "../resources/StatusResource";
import {
    LOADING_FAILED,
    RESPONSE_KEY_DATA,
    RESPONSE_KEY_DEVICE_HEALTH_STATUS_LIST
} from "../util/Constants";
import { isResponseCode5xx, logOperationalMetric } from "../util/Util";

/**
 * Shared function to fetch device health status and map to model.
 * Reusable across ADTA, ADTF, ASL, ASML by passing deviceType.
 *
 * @param {Object} model - Device model with macAddressArray and deviceInformation map
 * @param {string} deviceType - e.g., "ADTA", "ADTF", "ASL", "ASML"
 * @param {string} deviceInfoKey - Key in model for device info map (e.g., "adtaDeviceInformation")
 * @param {string} metricProcessName - Metric name for logging
 * @returns {Object} { isSuccess, isFault }
 */
export async function fetchAndMapDeviceHealthStatus(model, deviceType, deviceInfoKey, metricProcessName) {
    const startTime = Date.now();
    const deviceDetailsList = buildDeviceDetailsList(model, deviceType, deviceInfoKey);
    const response = await getDeviceHealthStatus(deviceDetailsList);
    const isSuccess = response.status === 200;
    const isFault = isResponseCode5xx(response.status);

    if (isSuccess) {
        parseAndMapResponse(response, model, deviceInfoKey);
    } else {
        handleHealthStatusError(model, deviceInfoKey);
    }

    logOperationalMetric(startTime, metricProcessName, "getDeviceHealthStatusDetails", isSuccess, isFault);
    return { isSuccess, isFault };
}

/**
 * Build deviceDetailsList from model for the GetDeviceHealthStatus request.
 * Each entry contains deviceIdentifier (mac), deviceType, and optional deviceSubType.
 */
function buildDeviceDetailsList(model, deviceType, deviceInfoKey) {
    return model.macAddressArray.map((mac) => {
        const device = model[deviceInfoKey][mac];
        const details = {
            deviceIdentifier: mac,
            deviceType: deviceType
        };
        if (device && device.deviceSubType) {
            details.deviceSubType = device.deviceSubType;
        }
        return details;
    });
}

/**
 * Parse GetDeviceHealthStatus response and map to device model fields.
 */
function parseAndMapResponse(response, model, deviceInfoKey) {
    if (RESPONSE_KEY_DATA in response && RESPONSE_KEY_DEVICE_HEALTH_STATUS_LIST in response.data) {
        response.data.deviceHealthStatusList.forEach((deviceStatus) => {
            const device = model[deviceInfoKey][deviceStatus.deviceIdentifier];
            if (device) {
                device.assetStatus = deviceStatus.connectionStatus;
                device.pingStatus = deviceStatus.pingStatus;
                device.lastConnectionTime = deviceStatus.lastConnectionTime;
                device.recentSuccessfulPingTime = deviceStatus.recentSuccessfulPingTime;
                device.troubleShootHint = deviceStatus.troubleShootHint;
            }
        });
    }
}

/**
 * Set error state for all devices when API fails.
 */
function handleHealthStatusError(model, deviceInfoKey) {
    model.macAddressArray.forEach((mac) => {
        const device = model[deviceInfoKey][mac];
        if (device) {
            device.assetStatus = LOADING_FAILED;
            device.pingStatus = LOADING_FAILED;
        }
    });
}



// WEBPACK FOOTER //
// ./src/components/commonui/DeviceHealthStatusHelper.js